import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUIContext } from "../../../../context/UIContext";
import useSurveyResponses from "../../../../hooks/useSurveyResponses";
import ResponseQuestion from "./ResponseQuestion";
import styles from "../../../adminRelated/surveySetting/components/SurveyPreview.module.css";

export default function SurveyForm({
  survey,
  questions,
  subjectId,
  subjectName,
  subjectInfo,
  schoolYear,
  schoolTerm,
  currentPeriod,
  respondentUsername,
}) {
  const { t } = useTranslation();
  const { handleToastCenterTop } = useUIContext();

  const [responses, setResponses] = useState({});
  const [surveyResponse, setSurveyResponse] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [canModify, setCanModify] = useState(true); // 수정 가능 여부

  // Hook 사용 - createOrUpdateResponse와 updateSurveyResponse 사용
  const { createOrUpdateResponse, updateSurveyResponse } = useSurveyResponses();

  // 기존 응답 조회 조건
  const existingResponseConditions = {
    schoolid: survey?.schoolid,
    survey_id: survey?.id,
    subject_id: subjectId,
  };

  // 익명 설문이 아닌 경우에만 username 조건 추가
  if (!survey?.is_anonymous) {
    existingResponseConditions.username = respondentUsername;
  }

  const existingResponsesQuery = useSurveyResponses(
    survey?.schoolid &&
      survey?.id &&
      subjectId &&
      (survey?.is_anonymous || respondentUsername) // 익명 설문이거나 username이 있는 경우
      ? existingResponseConditions
      : {}
  );

  // 기존 응답 조회 및 초기화
  useEffect(() => {
    if (questions?.length > 0) {
      // 응답 초기화 - 활성화된 질문만
      const initialResponses = {};
      questions
        .filter((question) => question.is_active !== false)
        .forEach((question) => {
          if (question.question_type === "multiple_choice") {
            initialResponses[question.id] = { optionId: "", reason: "" };
          } else if (question.question_type === "checkbox") {
            initialResponses[question.id] = { optionIds: [], reasons: {} };
          } else {
            initialResponses[question.id] = "";
          }
        });
      setResponses(initialResponses);
    }
  }, [questions]);

  // 기존 응답 데이터 로드
  useEffect(() => {
    const existingResponses = existingResponsesQuery.surveyResponsesQuery?.data;

    if (existingResponses?.length > 0) {
      const existingResponse = existingResponses[0]; // 첫 번째 응답 사용
      setSurveyResponse(existingResponse);
      setIsSubmitted(existingResponse.is_submitted);

      // 기존 답변들을 폼에 반영 - response.answers 배열에서 직접 가져오기
      if (existingResponse.answers?.length > 0 && questions?.length > 0) {
        const loadedResponses = {};

        existingResponse.answers.forEach((answer) => {
          const questionId = answer.question_id;
          const question = questions.find(
            (q) => q.id === questionId && q.is_active !== false
          );

          if (question) {
            if (question.question_type === "multiple_choice") {
              // 객관식: selected_option_id를 문자열로 변환하고 reason도 포함
              const responseData = {
                optionId: answer.selected_option_id?.toString() || "",
              };
              if (answer.option_reason) {
                // JSON 형태의 option_reason에서 해당 옵션의 사유 추출
                if (typeof answer.option_reason === "object") {
                  const optionId = answer.selected_option_id?.toString();
                  responseData.reason = answer.option_reason[optionId] || "";
                } else {
                  // 기존 문자열 형태 호환성 유지
                  responseData.reason = answer.option_reason;
                }
              }
              loadedResponses[questionId] = responseData;
            } else if (question.question_type === "checkbox") {
              // 체크박스: selected_checkbox_ids 배열에서 가져오고 reasons도 포함
              const responseData = {
                optionIds: answer.selected_checkbox_ids || [],
              };
              if (answer.option_reason) {
                if (typeof answer.option_reason === "object") {
                  // JSON 형태의 option_reason을 그대로 사용
                  responseData.reasons = answer.option_reason;
                } else {
                  // 기존 문자열 형태 호환성 유지
                  const reasonParts = answer.option_reason.split(", ");
                  const selectedIds = answer.selected_checkbox_ids || [];
                  responseData.reasons = {};
                  selectedIds.forEach((id, index) => {
                    if (reasonParts[index] && reasonParts[index].trim()) {
                      responseData.reasons[id] = reasonParts[index].trim();
                    }
                  });
                }
              }
              loadedResponses[questionId] = responseData;
            } else {
              // 단답형, 장문형: answer_text 사용
              loadedResponses[questionId] = answer.answer_text || "";
            }
          }
        });

        setResponses((prev) => ({ ...prev, ...loadedResponses }));
      }
    }
  }, [existingResponsesQuery.surveyResponsesQuery?.data, questions]);

  // 마감일 및 설문 상태 체크
  useEffect(() => {
    if (survey && currentPeriod) {
      const endDate = new Date(currentPeriod.end_date);
      const deadline = new Date(endDate);
      deadline.setDate(deadline.getDate() + (survey.deadline || 0));

      const now = new Date();
      const isWithinDeadline = now <= deadline;

      // 설문 상태에 따른 권한 설정
      const isRespondable = survey.status === "respondable";

      setCanSubmit(isWithinDeadline && isRespondable);
      setCanModify(isRespondable); // respondable일 때만 수정 가능
    }
  }, [survey, currentPeriod]);

  // 응답 변경 핸들러
  const handleResponseChange = (questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  // 응답 저장 함수 (임시저장/제출 공통)
  const saveResponse = async (isSubmit = false) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      // 답변 데이터 생성
      const answers = Object.entries(responses)
        .filter(([_, value]) => {
          // 빈 값 필터링
          if (value === "" || value === null || value === undefined)
            return false;

          // 객관식: optionId가 있는지 체크
          if (value.optionId !== undefined) {
            return value.optionId !== "" && value.optionId !== null;
          }

          // 체크박스: optionIds 배열이 비어있지 않은지 체크
          if (value.optionIds !== undefined) {
            return Array.isArray(value.optionIds) && value.optionIds.length > 0;
          }

          // 기타 타입: 빈 배열 체크
          if (Array.isArray(value) && value.length === 0) return false;

          return true;
        })
        .map(([questionId, value]) => {
          const question = questions.find(
            (q) => q.id.toString() === questionId && q.is_active !== false
          );

          let answerData = {
            schoolid: survey.schoolid,
            question_id: parseInt(questionId),
            subject_id: subjectId,
            survey_title: survey.title,
            survey_question_title: question?.question_text,
            subject_name: subjectName,
            school_year: schoolYear,
            school_term: schoolTerm,
          };

          // 익명 설문이 아닌 경우에만 username 포함
          if (!survey.is_anonymous) {
            answerData.username = respondentUsername;
          }

          // 질문 타입에 따라 데이터 처리
          if (question?.question_type === "multiple_choice") {
            // 객관식: selected_option_id와 선택한 옵션 텍스트를 answer_text에 저장
            answerData.selected_option_id = parseInt(value.optionId || value);

            // 선택한 옵션의 텍스트 찾기
            const selectedOption = question.options?.find(
              (opt) => opt.id === parseInt(value.optionId || value)
            );
            answerData.answer_text = selectedOption
              ? selectedOption.option_text
              : null;

            // option_reason이 있으면 JSON 형태로 저장
            if (value.reason && value.reason.trim()) {
              answerData.option_reason = {
                [value.optionId || value]: value.reason.trim(),
              };
            }

            answerData.selected_checkbox_ids = null;
          } else if (question?.question_type === "checkbox") {
            // 체크박스: selected_checkbox_ids 배열에 저장하고, 선택한 옵션들의 텍스트를 answer_text에 저장
            const selectedIds = Array.isArray(value.optionIds)
              ? value.optionIds
              : Array.isArray(value)
              ? value
              : [value];
            answerData.selected_checkbox_ids = selectedIds;

            // 선택한 옵션들의 텍스트 찾기
            const selectedTexts = selectedIds
              .map((id) => {
                const option = question.options?.find(
                  (opt) => opt.id === parseInt(id)
                );
                return option ? option.option_text : null;
              })
              .filter((text) => text !== null);

            answerData.answer_text =
              selectedTexts.length > 0 ? selectedTexts.join(", ") : null;

            // option_reason이 있으면 JSON 형태로 저장 (선택된 옵션들만)
            if (value.reasons && Object.keys(value.reasons).length > 0) {
              const reasonObj = {};
              selectedIds.forEach((id) => {
                const reason = value.reasons[id];
                if (reason !== undefined) {
                  reasonObj[id] = reason.trim();
                }
              });

              if (Object.keys(reasonObj).length > 0) {
                answerData.option_reason = reasonObj;
              }
            }

            answerData.selected_option_id = null;
          } else {
            // 단답형, 장문형: answer_text에 저장
            answerData.answer_text = value;
            answerData.selected_option_id = null;
            answerData.selected_checkbox_ids = null;
          }

          return answerData;
        });

      // 응답 데이터 생성
      const responseData = {
        id: surveyResponse?.id, // 기존 응답이 있으면 ID 포함
        schoolid: survey.schoolid,
        survey_id: survey.id,
        subject_id: subjectId,
        school_year: schoolYear,
        school_term: schoolTerm,
        is_submitted: isSubmit,
        answers: answers, // 답변 배열 포함
      };

      // 익명 설문이 아닌 경우에만 username 포함
      if (!survey.is_anonymous) {
        responseData.username = respondentUsername;
      }

      // 제출 시에만 submitted_at 설정
      if (isSubmit) {
        responseData.submitted_at = new Date().toISOString();
      }

      const result = await createOrUpdateResponse.mutateAsync(responseData);

      // 결과가 ID만 반환되는 경우 처리
      if (typeof result === "number" && !surveyResponse) {
        setSurveyResponse({ id: result });
      }

      if (isSubmit) {
        setIsSubmitted(true);
      }

      return true;
    } catch (error) {
      console.error("응답 저장 오류:", error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // 임시저장 핸들러
  const handleTempSave = async () => {
    if (!canModify) {
      handleToastCenterTop(
        t("common.survey.disabled", "응답 불가"),
        3000,
        "#dc3545"
      );
      return;
    }

    try {
      await saveResponse(false);
      handleToastCenterTop(
        t("common.survey.tempSaveSuccess", "임시저장이 완료되었습니다."),
        3000,
        "#28a745"
      );
    } catch (error) {
      handleToastCenterTop(
        t("common.survey.tempSaveError", "임시저장 중 오류가 발생했습니다."),
        3000,
        "#dc3545"
      );
    }
  };

  // 제출 취소 핸들러
  const handleCancelSubmission = async () => {
    if (!canModify) {
      handleToastCenterTop(
        t("common.survey.disabled", "응답 불가"),
        3000,
        "#dc3545"
      );
      return;
    }

    if (!surveyResponse) {
      handleToastCenterTop(
        t("common.survey.noResponseToCancel", "취소할 응답이 없습니다."),
        3000,
        "#dc3545"
      );
      return;
    }

    try {
      setIsSaving(true);

      // 제출 상태만 간단하게 업데이트
      const cancelData = {
        id: surveyResponse.id,
        is_submitted: false,
      };

      // 익명 설문이 아닌 경우에만 updatedBy 포함
      if (!survey.is_anonymous) {
        cancelData.updatedBy = respondentUsername;
      }

      await updateSurveyResponse.mutateAsync(cancelData);

      setIsSubmitted(false);
      handleToastCenterTop(
        t("common.survey.cancelSuccess", "제출이 성공적으로 취소되었습니다."),
        3000,
        "#28a745"
      );
    } catch (error) {
      console.error("제출 취소 오류:", error);
      handleToastCenterTop(
        t("common.survey.cancelError", "제출 취소 중 오류가 발생했습니다."),
        3000,
        "#dc3545"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // 제출 핸들러
  const handleSubmit = async () => {
    if (!canModify) {
      handleToastCenterTop(
        t("common.survey.disabled", "응답 불가"),
        3000,
        "#dc3545"
      );
      return;
    }

    if (!canSubmit) {
      const message =
        survey.status !== "respondable"
          ? t("common.survey.disabled", "응답 불가")
          : t("common.survey.deadlineExpired", "응답 기간이 만료되었습니다.");
      handleToastCenterTop(message, 3000, "#dc3545");
      return;
    }

    try {
      // 필수 질문 체크 - 활성화된 질문만
      const requiredQuestions = questions.filter(
        (q) => q.is_required && q.is_active !== false
      );
      const missingRequired = requiredQuestions.some((q) => {
        const response = responses[q.id];

        // 빈 응답 체크
        if (!response) return true;

        // 질문 타입별 체크
        if (q.question_type === "multiple_choice") {
          return !response.optionId || response.optionId === "";
        } else if (q.question_type === "checkbox") {
          return (
            !response.optionIds ||
            !Array.isArray(response.optionIds) ||
            response.optionIds.length === 0
          );
        } else {
          return !response || response === "";
        }
      });

      if (missingRequired) {
        handleToastCenterTop(
          t("common.survey.missingRequired", "필수 질문에 답변해주세요."),
          3000,
          "#dc3545"
        );
        return;
      }

      await saveResponse(true);
      handleToastCenterTop(
        t("common.survey.submitSuccess", "설문이 성공적으로 제출되었습니다."),
        3000,
        "#28a745"
      );
    } catch (error) {
      handleToastCenterTop(
        t("common.survey.submitError", "제출 중 오류가 발생했습니다."),
        3000,
        "#dc3545"
      );
    }
  };

  if (!questions || questions.length === 0) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>
          {t("common.survey.loadingQuestions", "설문 질문을 불러오는 중...")}
        </p>
      </div>
    );
  }

  const themeClass = styles[survey.theme] || styles.classic;

  return (
    <div className={`${styles.previewContainer} ${themeClass}`}>
      {/* 설문 정보 */}
      <div className={styles.surveyHeader}>
        <h1 className={styles.surveyTitle}>{survey.title}</h1>
        {survey.description && (
          <p className={styles.surveyDescription}>{survey.description}</p>
        )}

        <div className={styles.surveyMeta}>
          <span className={styles.metaItem}>
            {survey.is_anonymous ? "🎭 익명" : "📝 실명"}
          </span>
          <span className={styles.metaItem}>📚 {subjectName}</span>
          <span className={styles.metaItem}>
            {" "}
            👨‍🏫{" "}
            {subjectInfo?.lesson?.teachers?.map((t) => t.tname).join(", ") ||
              "교사 정보 없음"}
          </span>
          <span className={styles.metaItem}>
            📅 {schoolYear}-{schoolTerm}
          </span>
          <span className={styles.metaItem}>
            📝 {questions.filter((q) => q.is_active !== false).length}개 질문
          </span>
          {!canSubmit && survey.status === "respondable" && (
            <span className={`${styles.metaItem} text-danger`}>
              ⚠️ {t("common.survey.expired", "응답기간 만료")}
            </span>
          )}
          {survey.status === "readable" && (
            <span className={`${styles.metaItem} text-warning`}>
              👁️ 조회만 가능
            </span>
          )}
          {isSubmitted && (
            <span className={`${styles.metaItem} text-success`}>
              ✅ {t("common.survey.submittedStatus", "제출 완료")}
            </span>
          )}
        </div>
        {subjectInfo?.extraAttributes?.subjectContent && (
          <p
            className={styles.surveyDescription}
            style={{ marginTop: "10px", marginBottom: "0px" }}
          >
            [공통내용] {subjectInfo?.extraAttributes?.subjectContent}
          </p>
        )}
      </div>

      {/* 질문 목록 */}
      <form className={styles.surveyForm} onSubmit={(e) => e.preventDefault()}>
        {questions
          .filter((question) => question.is_active !== false) // 비활성화된 질문 제외
          .map((question, index) => (
            <ResponseQuestion
              key={question.id}
              question={question}
              index={index}
              value={responses[question.id]}
              onChange={(value) => handleResponseChange(question.id, value)}
              themeStyles={styles}
              disabled={isSubmitted || !canModify}
            />
          ))}

        {/* 버튼 섹션 */}
        <div className={styles.submitSection}>
          {isSubmitted ? (
            <div className="alert alert-success">
              <h5>✅ {t("common.survey.submitted", "제출 완료")}</h5>
              <p>
                {t(
                  "common.survey.submittedMessage",
                  "설문 응답이 성공적으로 제출되었습니다."
                )}
              </p>
              {/* 제출 취소 버튼 추가 */}
              <div className="d-flex justify-content-center mt-3">
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={handleCancelSubmission}
                  disabled={isSaving || !canModify}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      {t("common.survey.canceling", "취소 중...")}
                    </>
                  ) : (
                    <>🔄 {t("common.survey.cancelSubmission", "제출 취소")}</>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="d-flex gap-3 justify-content-center">
              {/* 임시저장 버튼 */}
              <button
                type="button"
                className={styles.tempSaveButton}
                onClick={handleTempSave}
                disabled={!canModify || isSaving}
              >
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    {t("common.survey.saving", "저장 중...")}
                  </>
                ) : (
                  <>💾 {t("common.survey.tempSave", "임시저장")}</>
                )}
              </button>

              {/* 제출 버튼 */}
              <button
                type="button"
                className={styles.submitButton}
                onClick={handleSubmit}
                disabled={!canSubmit || !canModify}
              >
                📤 {t("common.survey.submitResponse", "제출하기")}
              </button>
            </div>
          )}

          {(!canSubmit || !canModify) && (
            <p className="text-danger mt-3 text-center">
              {!canModify
                ? survey.status === "readable"
                  ? "조회만 가능한 설문입니다."
                  : t("common.survey.disabled", "응답 불가")
                : t(
                    "common.survey.cannotSubmit",
                    "응답 기간이 만료되어 제출할 수 없습니다."
                  )}
            </p>
          )}
        </div>
      </form>
    </div>
  );
}
